import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Target, Plus, Trash, CheckCircle } from "@phosphor-icons/react";

const METRICS = [
  { value: "avg_sleep", label: "Средний сон за месяц (ч)" },
  { value: "avg_recovery", label: "Среднее восстановление (%)" },
  { value: "green_days", label: "Зелёных дней за месяц" },
  { value: "tasks_done", label: "Выполненных задач за месяц" },
];

export default function GoalsCard() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [metric, setMetric] = useState("avg_sleep");
  const [target, setTarget] = useState("");

  const load = () => api.get("/goals").then((r) => setGoals(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = () => {
    const t = parseFloat(target);
    if (!t || t <= 0) return toast.error("Укажите цель числом");
    api.post("/goals", { metric, target: t }).then(() => {
      toast.success("Цель добавлена");
      setOpen(false);
      setTarget("");
      load();
    });
  };

  const remove = (id) => api.delete(`/goals/${id}`).then(load);

  return (
    <div className="glass-card p-5" data-testid="goals-card">
      <div className="flex items-center gap-2 mb-3">
        <Target size={18} weight="fill" className="text-[#ffea00]" />
        <h3 className="text-sm font-semibold">Цели месяца</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="ml-auto h-7 px-2.5" data-testid="goal-add-button">
              <Plus size={14} className="mr-1" /> Цель
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogTitle className="text-base">Новая цель на месяц</DialogTitle>
            <DialogDescription className="text-xs">Выберите метрику и целевое значение — прогресс считается автоматически.</DialogDescription>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger data-testid="goal-metric-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METRICS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`goal-metric-${m.value}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="number" step="0.1" value={target} onChange={(e) => setTarget(e.target.value)}
              placeholder="Целевое значение, напр. 7.5" data-testid="goal-target-input" />
            <Button onClick={add} data-testid="goal-save-button">Добавить</Button>
          </DialogContent>
        </Dialog>
      </div>
      {goals.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="goals-empty">
          Поставьте цель — например, «средний сон 7,5 ч» или «15 зелёных дней» — и следите за прогрессом.
        </p>
      ) : (
        <div className="space-y-3">
          {goals.map((g) => (
            <div key={g.id} data-testid={`goal-row-${g.metric}`}>
              <div className="flex items-center gap-2 text-xs mb-1">
                {g.done && <CheckCircle size={14} weight="fill" className="text-[#00e676]" />}
                <span className={g.done ? "text-[#00e676] font-semibold" : ""}>{g.label}</span>
                <span className="ml-auto num text-muted-foreground">
                  {g.current ?? "—"} / {g.target} {g.unit}
                </span>
                <button onClick={() => remove(g.id)} data-testid={`goal-delete-${g.metric}`}
                  className="text-muted-foreground hover:text-destructive transition-colors"><Trash size={13} /></button>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${g.pct ?? 0}%`, background: g.done ? "#00e676" : "#ffea00" }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
